import type { Workspace } from '@up2stay/up2promo-api-client';
import { Button } from '@up2stay/up2promo-ui';
import { LayoutGroup } from 'motion/react';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { CreateWorkspaceWizard } from '@/core/components/onboarding/create-workspace-wizard';
import { PageHeader } from '@/core/components/page-header';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import {
  goBackOrFallback,
  useIonBackRouter,
} from '@/core/lib/ion-back-navigation';
import { readWorkspaceCreateReturnTo } from '@/core/lib/workspace-create-return';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useWorkspaceStore } from '@/core/store/workspace.store';
import { AppIonPage } from '@/core/components/app-ion-page';

function CreateWorkspaceBreadcrumb({
  domainLabel,
  createLabel,
}: {
  domainLabel: string;
  createLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {createLabel}
    </>
  );
}

/**
 * In-shell “add another workspace” flow. Full-page WizardShell (shell
 * variant) — same density as Create Location / product setup. First-time
 * zero-workspace onboarding stays on `/onboarding`.
 */
export function CreateWorkspacePage() {
  const dict = useDictionary();
  const backRouter = useIonBackRouter();
  const { navigateShell } = useNavigateShell();
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);

  const location = useLocation();
  const returnTo = readWorkspaceCreateReturnTo(location.search);

  const leavePage = useCallback(() => {
    goBackOrFallback(backRouter, {
      href: returnTo ?? shellPaths.home,
      navigate: () =>
        returnTo
          ? navigateShell(returnTo)
          : navigateShell(shellPaths.home, { intent: 'switchTab' }),
    });
  }, [backRouter, navigateShell, returnTo]);

  const handleCreated = useCallback(
    (workspace: Workspace) => {
      setWorkspace(workspace);
      navigateShell(shellPaths.home, { intent: 'switchTab' });
    },
    [navigateShell, setWorkspace],
  );

  const createCopy = dict.workspace.create;
  const title = createCopy?.title ?? dict.workspace.createWorkspace;
  const breadcrumb = (
    <CreateWorkspaceBreadcrumb
      domainLabel={dict.nav.navDomainWorkspaceAdmin}
      createLabel={dict.workspace.createWorkspace}
    />
  );

  return (
    <AppIonPage>
      <AppPageContent scrollY={false}>
        <div className="relative flex h-full min-h-0 flex-col">
          <AppPageLayout
            withGap={false}
            className="flex min-h-0 flex-1 flex-col"
          >
            <PageHeader
              breadcrumb={breadcrumb}
              title={title}
              onBack={leavePage}
              actions={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={leavePage}
                  className="text-muted-foreground"
                >
                  {createCopy?.cancel ?? dict.onboarding.cancel}
                </Button>
              }
            />
            <LayoutGroup id="create-workspace-page">
              <CreateWorkspaceWizard
                mode="create"
                variant="shell"
                onCreated={handleCreated}
              />
            </LayoutGroup>
          </AppPageLayout>
        </div>
      </AppPageContent>
    </AppIonPage>
  );
}
